import {
  createTransaction,
  createRecentActivity,
  type Transaction,
} from '@/data/entities/residents/finance/transactionData';

export interface FacilityPaymentData {
  facilityId: string;
  facilityName: string;
  bookingId: string;
  userId: string;
  citizenId: string;
  fullToolboxId: string;
  amount: number;
  paymentMethodId: string;
  paymentMethodType: 'bank' | 'card' | 'wallet' | 'crypto';
  paymentMethodLabel: string;
  bookingDate: string;
  startTime: string;
  endTime: string;
  purpose: string;
  attendeeCount: number;
  specialRequests?: string;
}

export interface FacilityPaymentResult {
  success: boolean;
  transactionId?: string;
  confirmationNumber?: string;
  error?: string;
}

/**
 * Service to handle facility booking payments and integrate with the transaction system
 */
class FacilityPaymentService {
  /**
   * Process a facility booking payment and record it in the transaction system
   */
  async processPayment(paymentData: FacilityPaymentData): Promise<FacilityPaymentResult> {
    try {
      // Create transaction metadata for facility booking
      const metadata = {
        facilityId: paymentData.facilityId,
        facilityName: paymentData.facilityName,
        bookingId: paymentData.bookingId,
        bookingDate: paymentData.bookingDate,
        startTime: paymentData.startTime,
        endTime: paymentData.endTime,
        purpose: paymentData.purpose,
        attendeeCount: paymentData.attendeeCount.toString(),
        specialRequests: paymentData.specialRequests || '',
        category: 'fees' as const,
        description: `Facility Booking - ${paymentData.facilityName}`,
      };

      // Create transaction record
      const transaction = createTransaction(
        `inv_facility_${paymentData.bookingId}`,
        paymentData.amount,
        paymentData.paymentMethodId,
        paymentData.paymentMethodType,
        paymentData.userId,
        paymentData.citizenId,
        paymentData.fullToolboxId,
        metadata,
      );

      // Actually store the transaction in the transactions array
      const { transactions } = await import('@/data/entities/residents/finance/transactionData');
      transactions.push(transaction);

      // Create recent activity record
      const activity = createRecentActivity(transaction, paymentData.paymentMethodLabel);

      // Store the activity in the activities array
      const { recentActivities } =
        await import('@/data/entities/residents/finance/transactionData');
      recentActivities.push(activity);

      // In a real implementation, this would:
      // 1. Call payment processor API
      // 2. Update booking status in database
      // 3. Send confirmation email
      // 4. Update facility availability

      // Simulate payment processing delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Mark transaction as completed (in real app, this would happen after payment confirmation)
      transaction.status = 'completed';
      transaction.completedAt = new Date().toISOString();
      transaction.updatedAt = new Date().toISOString();

      return {
        success: true,
        transactionId: transaction.id,
        confirmationNumber: transaction.confirmationNumber,
      };
    } catch (error) {
      console.error('Facility payment processing failed:', error);

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Payment processing failed',
      };
    }
  }

  /**
   * Get facility booking transactions for a user
   */
  async getUserFacilityTransactions(userId: string): Promise<Transaction[]> {
    try {
      // In a real implementation, this would query the database
      // For now, we'll return mock data filtered from the transaction data
      const { getTransactionsByUser } =
        await import('@/data/entities/residents/finance/transactionData');
      const userTransactions = getTransactionsByUser(userId);

      // Filter for facility-related transactions
      return userTransactions.filter(
        (transaction) =>
          (transaction.category === 'fees' || transaction.category === 'other') &&
          transaction.metadata?.facilityId,
      );
    } catch (error) {
      console.error('Failed to fetch facility transactions:', error);
      return [];
    }
  }

  /**
   * Get a specific facility booking transaction
   */
  async getFacilityTransaction(transactionId: string): Promise<Transaction | null> {
    try {
      // In a real implementation, this would query the database
      const { transactions } = await import('@/data/entities/residents/finance/transactionData');
      return transactions.find((t) => t.id === transactionId) || null;
    } catch (error) {
      console.error('Failed to fetch facility transaction:', error);
      return null;
    }
  }

  /**
   * Cancel a facility booking payment (issue refund)
   */
  async refundPayment(transactionId: string, reason: string): Promise<FacilityPaymentResult> {
    try {
      const originalTransaction = await this.getFacilityTransaction(transactionId);

      if (!originalTransaction) {
        return {
          success: false,
          error: 'Original transaction not found',
        };
      }

      // Create refund transaction
      const refundTransaction = createTransaction(
        `refund_${originalTransaction.invoiceId}`,
        originalTransaction.amount,
        originalTransaction.paymentMethodId,
        originalTransaction.paymentMethodType,
        originalTransaction.userId,
        originalTransaction.citizenId,
        originalTransaction.fullToolboxId,
        {
          ...originalTransaction.metadata,
          refundReason: reason,
          originalTransactionId: originalTransaction.id,
          category: 'other' as const,
          description: `Refund - ${originalTransaction.description}`,
        },
      );

      // Mark refund as completed
      const completedRefund = {
        ...refundTransaction,
        type: 'refund' as const,
        status: 'completed' as const,
        completedAt: new Date().toISOString(),
      };

      return {
        success: true,
        transactionId: completedRefund.id,
        confirmationNumber: completedRefund.confirmationNumber,
      };
    } catch (error) {
      console.error('Facility payment refund failed:', error);

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Refund processing failed',
      };
    }
  }

  /**
   * Format facility booking for receipt display
   */
  formatReceiptData(transaction: Transaction) {
    if (!transaction.metadata?.facilityId) {
      throw new Error('Not a facility booking transaction');
    }

    return {
      transactionId: transaction.id,
      bookingId: transaction.metadata.bookingId as string,
      facilityName: transaction.metadata.facilityName as string,
      bookingDate: transaction.metadata.bookingDate as string,
      startTime: transaction.metadata.startTime as string,
      endTime: transaction.metadata.endTime as string,
      purpose: transaction.metadata.purpose as string,
      attendeeCount: parseInt(transaction.metadata.attendeeCount as string),
      amount: transaction.amount,
      paymentMethod: transaction.paymentMethodType,
      status: transaction.status,
      paidAt: transaction.completedAt || transaction.createdAt,
      confirmationNumber: transaction.confirmationNumber,
    };
  }
}

export const facilityPaymentService = new FacilityPaymentService();
export default facilityPaymentService;
